/**
 * useQuoteEta: asks the BFF for the promise window of the current basket.
 *
 * Promise Time: the server quotes, the client renders. The request is keyed
 * by its JSON so a re-render with identical content never re-quotes, and a
 * change to the basket, mode, address, area or scheduled time does (after a
 * short debounce, so typing a time does not fire a request per keystroke).
 */
import { useEffect, useMemo, useState } from "react";

import { quoteApi } from "@/api/services";
import type { QuoteEtaRequest, QuoteEtaResponse } from "@/api/online-ordering-service/model";

export type UseQuoteEtaOptions = {
  /** Milliseconds to wait after the last change before quoting. Default 400. */
  debounceMs?: number;
};

export type UseQuoteEtaResult = {
  quote: QuoteEtaResponse | null;
  loading: boolean;
  error: Error | null;
};

const IDLE: UseQuoteEtaResult = { quote: null, loading: false, error: null };

export function useQuoteEta(
  request: QuoteEtaRequest | null,
  opts: UseQuoteEtaOptions = {},
): UseQuoteEtaResult {
  const debounceMs = opts.debounceMs ?? 400;
  const key = useMemo(() => (request && request.branchId ? JSON.stringify(request) : ""), [request]);
  const [state, setState] = useState<UseQuoteEtaResult>(IDLE);

  useEffect(() => {
    if (!key) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const timer = setTimeout(() => {
      quoteApi
        .quote(JSON.parse(key) as QuoteEtaRequest)
        .then((quote) => {
          if (!cancelled) setState({ quote, loading: false, error: null });
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setState({ quote: null, loading: false, error: err instanceof Error ? err : new Error(String(err)) });
          }
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [key, debounceMs]);

  return state;
}
